import React, { useState } from "react";
import PostCard from "./components/PostCard";
import PostModal from "./components/PostModal";
import Notification from "../../../common/Notification";
import { usePostStore } from "../../../../store/postStore";
import { PostAction } from "./interfaces/post.interface";
import { useAuthStore } from "../../../../auth/auth.store";

const CreatePost: React.FC = () => {
  const [postText, setPostText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [postAction, setPostAction] = useState<PostAction>("default");

  // Store
  const createPost = usePostStore((s) => s.createPost);
  const isSaving = usePostStore((s) => s.isSaving);

  // Auth
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.currentUser);

  const toggleEmojiPicker = () => setEmojiPickerOpen((v) => !v);

  const addEmoji = (emoji: string) =>
    setPostText((prev) => prev + emoji);

  const handleFilesSelect = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selected = Array.from(e.target.files ?? []);

    if (files.length + selected.length > 4) {
      showNotification("You can upload up to 4 files.");
      return;
    }

    const newPreviews: string[] = [];

    selected.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setPreviews((prev) => [
            ...prev,
            ev.target?.result as string,
          ]);
        };
        reader.readAsDataURL(file);
      } else {
        newPreviews.push(URL.createObjectURL(file));
      }
    });

    setFiles((prev) => [...prev, ...selected]);
    setPreviews((prev) => [...prev, ...newPreviews]);

    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const clearFiles = () => {
    setFiles([]);
    setPreviews([]);
  };

  const publishPost = async (text?: string) => {
  const body = (text ?? postText).trim();

  if (!body && files.length === 0) {
    showNotification("Please add text or a file to post");
    return;
  }

  if (!isAuthenticated) {
    showNotification("Please sign in to post.");
    return;
  }

  const post = await createPost({
    body: body || undefined,
    visibility: "public",
    media: files.length > 0 ? files : undefined,
  });

  if (post) {
    showNotification("Post published successfully! 🎉");
    setModalOpen(false);
    setPostText("");
    clearFiles();
  } else {
    showNotification("Failed to publish post. Please try again.");
  }
};

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  const openPostModal = (action: PostAction) => {
    setPostAction(action);
    setModalOpen(true);
  };

  // Guests: show sign-in prompt
  if (!isAuthenticated) {
    return (
      <div className="rounded-lg bg-white p-4 shadow dark:bg-deepSlate">
        <p className="text-center text-gray-600 dark:text-gray-300">
          <a
            href="/login"
            className="font-semibold text-primary-600 hover:underline"
          >
            Sign in
          </a>{" "}
          to post.
        </p>
      </div>
    );
  }

  return (
    <>
<PostCard
  onOpenModal={openPostModal}
  onSubmit={(content) => publishPost(content)}  // ← مرر النص
/>
      <PostModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        postText={postText}
        setPostText={setPostText}
        files={files}
        previews={previews}
        emojiPickerOpen={emojiPickerOpen}
        toggleEmojiPicker={toggleEmojiPicker}
        addEmoji={addEmoji}
        handleFilesSelect={handleFilesSelect}
        removeFile={removeFile}
        clearFiles={clearFiles}
        publishPost={publishPost}
        isSaving={isSaving}
      />

      {notification && <Notification message={notification} />}
    </>
  );
};

export default CreatePost;